import * as React from 'react';
import { css, SerializedStyles } from '@emotion/react';
import { SwitchBaseType } from '../../theming';
import { useSwitchContainer } from './SwitchContainer.context';

// The input fills its container, but is not visible.
const STATIC_STYLES: SerializedStyles = css`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    margin: 0;
    opacity: 0;
    z-index: 1;
    cursor: pointer;

    &:disabled,
    &[aria-disabled='true'] {
        pointer-events: none;
    }
`;

export interface SwitchBaseProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size' | 'width'> {
    /**
     * If `true`, the component will be checked.
     */
    checked?: boolean;

    /**
     * The default checked state to use if the component is not controlled.
     * @default false
     */
    defaultChecked?: boolean;

    /**
     * If `true`, the component will be disabled.
     */
    disabled?: boolean;

    /**
     * If `true`, the `input` element will have the `aria-invalid` attribute.
     */
    error?: boolean;

    /**
     * Callback fired when the `input` element loses focus.
     * @param {React.FocusEvent} event The event source of the callback.
     */
    onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;

    /**
     * Callback fired when the value of the `input` element is changed.
     * @param {React.ChangeEvent} event The event source of the callback.
     */
    onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;

    /**
     * Callback fired when the `input` element receives focus.
     * @param {React.FocusEvent} event The event source of the callback.
     */
    onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;

    /**
     * The `tabindex` attribute of the `input` element.
     * @default 0
     */
    tabIndex?: number;

    /**
     * The `type` attribute of the `input` element.
     * @default checkbox
     */
    type?: SwitchBaseType;

    /**
     * The `value` attribute of the `input` element.
     */
    value?: string;

    /**
     * If `true`, the element will have styles which make it invisble.
     * @default true
     */
    invisible?: boolean;

    /**
     * Allow any data-* attribute.
     */
    [key: `data-${string}`]: string;
}

export const SwitchBase: React.ForwardRefExoticComponent<SwitchBaseProps & React.RefAttributes<HTMLInputElement>> =
    React.forwardRef(function SwitchBase(
        props: SwitchBaseProps,
        ref: React.ForwardedRef<HTMLInputElement>
    ): React.JSX.Element {
        const {
            checked: checkedProp,
            defaultChecked,
            disabled = false,
            error = false,
            onBlur,
            onChange,
            onFocus,
            tabIndex = 0,
            type = 'checkbox',
            value,
            invisible = true,
            ...other
        } = props;

        const { setFocused, setChecked } = useSwitchContainer();

        const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
            onChange?.(event);
            setChecked?.(event.target.checked);
        };

        React.useEffect(() => {
            setChecked?.(checkedProp || defaultChecked || false);
        }, [checkedProp, defaultChecked, setChecked]);

        const handleBlur = (event: React.FocusEvent<HTMLInputElement>): void => {
            onBlur?.(event);
            setFocused?.(false);
        };

        const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
            onFocus?.(event);
            setFocused?.(true);
        };

        return (
            <input
                ref={ref}
                css={invisible ? STATIC_STYLES : null}
                aria-invalid={error || undefined}
                type={type}
                onBlur={handleBlur}
                onChange={handleChange}
                onFocus={handleFocus}
                disabled={disabled || undefined}
                tabIndex={disabled ? -1 : tabIndex}
                checked={checkedProp}
                defaultChecked={defaultChecked}
                value={value}
                {...other}
            />
        );
    });
